import { useEffect, useState, Fragment } from "react";
import api, { apiError, fmtUSD } from "../../../lib/api";
import { toast } from "sonner";

export function SPVs() {
  const [spvs, setSpvs] = useState([]);
  const [editing, setEditing] = useState(null);
  const [stmt, setStmt] = useState(null);
  const emptySpv = { name: "", description: "", finders_fee_type: "percent", finders_fee_value: "", status: "active" };
  const [f, setF] = useState(emptySpv);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/spvs").then((r) => setSpvs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openStatement = async (s) => {
    if (stmt?.spv?.spv_id === s.spv_id) {
      setStmt(null);
      return;
    }
    try {
      const { data } = await api.get(`/admin/spvs/${s.spv_id}/statement`);
      setStmt(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportPdf = async (s) => {
    try {
      const res = await api.get(`/admin/spvs/${s.spv_id}/statement.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `solera-statement-${s.name.toLowerCase().replace(/[ ,]+/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const startEdit = (s) => {
    setEditing(s.spv_id);
    setF({ name: s.name, description: s.description, finders_fee_type: s.finders_fee_type, finders_fee_value: String(s.finders_fee_value), status: s.status });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...f, finders_fee_value: Number(f.finders_fee_value || 0) };
      if (editing) await api.put(`/admin/spvs/${editing}`, payload);
      else await api.post("/admin/spvs", payload);
      toast.success(editing ? "SPV updated." : "SPV created.");
      setEditing(null);
      setF(emptySpv);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const feeLabel = (s) => {
    if (!(s.finders_fee_value > 0)) return "None";
    return s.finders_fee_type === "percent"
      ? `${s.finders_fee_value} percent of allocation`
      : `${fmtUSD(s.finders_fee_value)} per transaction`;
  };

  return (
    <div data-testid="admin-spvs">
      <div className="max-w-xl space-y-3" data-testid="spv-form">
        <span className="t-label">{editing ? "Edit SPV" : "Create an SPV vehicle"}</span>
        <p className="t-cap text-muted-s">Deals are attached to a vehicle from the Deals tab. The finders fee is shown to members on the deal terms.</p>
        <input className="input-solera" placeholder="Name, e.g. Solera SPV III, LP" value={f.name}
          onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="spv-name-input" />
        <input className="input-solera" placeholder="Description (internal)" value={f.description}
          onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="spv-description-input" />
        <div className="grid grid-cols-2 gap-3">
          <select className="input-solera" value={f.finders_fee_type}
            onChange={(e) => setF({ ...f, finders_fee_type: e.target.value })} data-testid="spv-fee-type-select">
            <option value="percent">Finders fee, percent of allocation</option>
            <option value="fixed">Finders fee, fixed amount USD</option>
          </select>
          <input className="input-solera fig" type="number" step="0.1" placeholder={f.finders_fee_type === "percent" ? "Percent, e.g. 1" : "Amount USD, e.g. 10000"}
            value={f.finders_fee_value} onChange={(e) => setF({ ...f, finders_fee_value: e.target.value })} data-testid="spv-fee-value-input" />
        </div>
        <select className="input-solera" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="spv-status-select">
          <option value="active">active</option><option value="closed">closed</option>
        </select>
        <div className="flex gap-3">
          {editing && <button className="btn-ghost" onClick={() => { setEditing(null); setF(emptySpv); }}>Cancel</button>}
          <button className="btn-primary" disabled={busy || !f.name} onClick={save} data-testid="spv-save-button">
            {editing ? "Save SPV" : "Create SPV"}
          </button>
        </div>
      </div>

      <table className="table-solera mt-12" data-testid="admin-spvs-table">
        <thead><tr><th>Vehicle</th><th>Description</th><th>Finders fee</th><th className="num">Deals</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {spvs.map((s) => (
            <Fragment key={s.spv_id}>
              <tr data-testid={`spv-row-${s.spv_id}`}>
                <td className="text-bone">{s.name}</td>
                <td className="text-muted-s t-small measure">{s.description}</td>
                <td className="text-muted-s t-small">{feeLabel(s)}</td>
                <td className="num text-bone">{s.deal_count}</td>
                <td className="text-muted-s">{s.status}</td>
                <td className="text-right whitespace-nowrap space-x-4">
                  <button onClick={() => openStatement(s)} className="t-label hover:!text-[#E8E6E1]" data-testid={`statement-spv-${s.spv_id}`}>
                    {stmt?.spv?.spv_id === s.spv_id ? "Hide" : "Statement"}
                  </button>
                  <button onClick={() => startEdit(s)} className="t-label hover:!text-[#E8E6E1]" data-testid={`edit-spv-${s.spv_id}`}>Edit</button>
                </td>
              </tr>
              {stmt?.spv?.spv_id === s.spv_id && (
                <tr>
                  <td colSpan={6}>
                    <div className="py-4" data-testid={`spv-statement-${s.spv_id}`}>
                      <div className="flex items-baseline justify-between gap-6">
                        <span className="t-label">Statement, {s.name}</span>
                        <button onClick={() => exportPdf(s)} className="t-label hover:!text-[#E8E6E1]" data-testid={`export-statement-${s.spv_id}`}>
                          Export PDF
                        </button>
                      </div>
                      <table className="table-solera mt-3">
                        <thead>
                          <tr><th>Deal</th><th>Finder</th><th className="num">Commitments</th><th className="num">Committed</th><th className="num">Allocated</th><th className="num">Finders fee</th></tr>
                        </thead>
                        <tbody>
                          {stmt.deals.map((d) => (
                            <tr key={d.deal_id}>
                              <td className="text-bone">{d.company} <span className="t-cap text-muted-s fig ml-2">{d.memo_code}</span></td>
                              <td className="text-muted-s t-small">{d.finder || "-"}</td>
                              <td className="num text-bone">{d.commitments_count}</td>
                              <td className="num text-bone">{fmtUSD(d.committed_total)}</td>
                              <td className="num text-bone">{fmtUSD(d.allocated_total)}</td>
                              <td className="num text-bone">{fmtUSD(d.fee)}</td>
                            </tr>
                          ))}
                          {stmt.deals.length === 0 && (
                            <tr><td colSpan={6} className="text-muted-s t-small py-4">No deals attached to this vehicle.</td></tr>
                          )}
                          {stmt.deals.length > 0 && (
                            <tr data-testid="statement-totals">
                              <td className="t-label">Totals</td><td></td>
                              <td className="num text-bone">{stmt.deals.reduce((a, d) => a + d.commitments_count, 0)}</td>
                              <td className="num text-bone">{fmtUSD(stmt.totals.committed)}</td>
                              <td className="num text-bone">{fmtUSD(stmt.totals.allocated)}</td>
                              <td className="num text-bone">{fmtUSD(stmt.totals.fees)}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      <p className="t-cap text-muted-s mt-2">
                        Fees are computed on allocated amounts only. Fixed fees count once per deal with at least one allocation.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
